import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Icon, CheckList } from './Primitives';
import { catalog, money, number, gross } from '../config/pricing';
import './UnityDialogs.css';

// Enquiries use the established contact channel. Account billing remains authoritative.
export default function UnityDialogs({ state, onClose, vatRate = 0.15 }) {
  const ref = useRef(null);
  const [pack, setPack] = useState(0);
  const mode = state?.mode;
  useEffect(() => {
    if (!mode) return undefined;
    setPack(0);
    const trigger = document.activeElement;
    const dialog = ref.current;
    dialog.showModal();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
      if (dialog.open) dialog.close();
      if (trigger?.isConnected) trigger.focus?.({ preventScroll: true });
    };
  }, [mode]);
  if (!mode) return null;

  const chosen = catalog.ai.topups[pack];
  const plan = catalog.plans.find((item) => item.id === state.planId);
  const trial = mode === 'trial';
  const topup = mode === 'topup';
  const title = topup ? 'More room to create.' : trial ? 'Your first programme starts here.' : 'Let’s shape your rollout.';
  const subject = trial ? 'Unity Learning trial enquiry' : 'Unity Learning rollout enquiry';
  const body = [
    'Hello AndUs team,',
    '',
    trial ? 'I would like to discuss a Unity Learning trial.' : 'I would like to discuss a Unity Learning demonstration and rollout.',
    ...(trial && plan ? [`Plan of interest: ${plan.name}`, `Billing preference: ${state.billingCycle === 'annual' ? 'Annual' : 'Monthly'}`] : []),
    'Please confirm availability, pricing and the next steps.',
    '',
    'Organisation: ',
    'Training needs: ',
  ].join('\n');
  const enquiryHref = `mailto:info@tpc.training?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

  const containTabFocus = (event) => {
    if (event.key !== 'Tab') return;
    const controls = Array.from(ref.current.querySelectorAll('a[href], button:not([disabled])'))
      .filter((element) => element.getClientRects().length > 0);
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  };

  return (
    <dialog ref={ref} className="ul-dialog" aria-labelledby="ul-dialog-title" aria-describedby="ul-dialog-intro"
      onKeyDown={containTabFocus}
      onCancel={(event) => { event.preventDefault(); onClose(); }}>
      <div className="ul-dialog-heading">
        <span className="ul-eyebrow">UNITY LEARNING</span>
        <button className="ul-dialog-close" type="button" onClick={onClose} aria-label="Close dialog"><Icon name="close" /></button>
      </div>
      <h2 id="ul-dialog-title">{title}</h2>
      <p className="ul-dialog-intro" id="ul-dialog-intro">
        {topup
          ? 'Explore indicative AI credit packs, then visit your account for available funding options and a confirmed quote.'
          : trial
            ? 'Tell us what you would like to create. Our team will confirm trial availability, your allowance and how to get started.'
            : 'Talk through programme creation, learner delivery and the reporting your organisation needs.'}
      </p>
      {topup ? <>
        <div className="ul-topup-packs" role="group" aria-label="Indicative AI credit packs">
          {catalog.ai.topups.map((item, index) => (
            <button key={item.priceZar} type="button" aria-pressed={pack === index} onClick={() => setPack(index)}>
              <b>{number(item.credits)}</b><span>credits</span>
              <strong>{money(item.priceZar * 100)} ex VAT</strong>
              {vatRate > 0 && <strong>{money(gross(item.priceZar * 100, vatRate))} incl VAT</strong>}
            </button>
          ))}
        </div>
        <div className="ul-topup-total">
          <span>{number(chosen.credits)} Unity AI credits</span>
          <b>{money(gross(chosen.priceZar * 100, vatRate))}</b>
          <small>Illustrative total {vatRate > 0 ? 'including VAT' : 'excluding VAT'}</small>
        </div>
        <p className="ul-note">Pack availability and credit allowances are subject to confirmation. Your account shows the funding options, wallet currency and final quote available to your workspace.</p>
        <Link className="ul-button" to="/unity/admin/billing">Open account billing <Icon size={18} /></Link>
        <p className="ul-dialog-foot">Sign in with workspace billing administrator access. Selecting a pack here does not create an order or change your balance.</p>
      </> : <>
        {trial && <>
          <div className="ul-trial-credit"><Icon name="spark" /><strong>14 days</strong><span>Ask about a free authoring trial</span></div>
          <CheckList items={['Start with your source material or an idea', 'Build and review a programme structure', 'Explore a complete outcome of learning content', 'Discuss the right plan for your organisation']} />
          {plan && <p className="ul-note">Your interest: <strong>{plan.name}</strong> · {state.billingCycle === 'annual' ? 'Annual billing' : 'Monthly billing'}</p>}
        </>}
        <a className="ul-button" href={enquiryHref}>Open email enquiry <Icon size={18} /></a>
        <p className="ul-dialog-foot">This opens a draft in your email app addressed to info@tpc.training. Review it and send when ready. Our team confirms access and terms before you start.</p>
      </>}
      <Link className="ul-small-link" to="/contactAndUs">View all contact options <Icon size={17} /></Link>
    </dialog>
  );
}
